import React, { useState } from 'react';

import { Mail } from 'lucide-react';
import { DateTime } from 'luxon';
import { queryCache, useMutation, useQuery } from 'react-query';
import { DotBricksLoading, Toast } from 'src/common/components';

import {
  archiveEdition,
  autoBuildEdition,
  createEdition,
  fetchEditions,
  fetchRecipientCohorts,
  restoreEdition,
} from '../api';
import {
  DEFAULT_EDITION_AUDIENCE,
  EDITION_AUDIENCE_OPTIONS,
  EDITIONS_QUERY_KEY,
  RECIPIENT_COHORTS_QUERY_KEY,
} from '../constants';
import { EditionAudience } from '../types';
import {
  baseCohortKey,
  customCohortKey,
  editionCohortKey,
  getEditionActionErrorMessage,
  resolveEditionAudience,
} from '../utils';

import EditionComposer from './EditionComposer';

type SubView = 'prep' | 'archived';

// A pill of the selector: Staff and Admins are the base cohorts and send as their
// audience; a custom cohort is created by id and goes out on its own send list.
type RecipientTarget = {
  key: string;
  label: string;
  audience: EditionAudience;
  customCohortId: number | null;
};

const BASE_TARGETS: RecipientTarget[] = EDITION_AUDIENCE_OPTIONS.map(({ id, label }) => ({
  key: baseCohortKey(id),
  label,
  audience: id,
  customCohortId: null,
}));

const UpcomingTab: React.FC = () => {
  const [subView, setSubView] = useState<SubView>('prep');

  const { data: allData, isLoading } = useQuery([EDITIONS_QUERY_KEY], fetchEditions);

  const allEditions = allData?.data.results ?? [];
  const prepEditions = allEditions.filter((e) => e.status !== 'sent' && e.status !== 'archived');
  const archivedEditions = allEditions.filter((e) => e.status === 'archived');

  const { data: cohortsData } = useQuery([RECIPIENT_COHORTS_QUERY_KEY], fetchRecipientCohorts);

  const customTargets: RecipientTarget[] = (cohortsData?.data.results ?? [])
    .filter((cohort) => cohort.kind === 'custom')
    .map((cohort) => ({
      key: customCohortKey(cohort.id),
      label: cohort.name,
      audience: cohort.send_list,
      customCohortId: cohort.id,
    }));
  // An unscheduled draft can outlive its cohort being archived; keep a pill for it so the draft
  // stays reachable and can still be archived or edited.
  const orphanTargets: RecipientTarget[] = prepEditions
    .filter(
      (edition) =>
        edition.cohort !== null && !customTargets.some((t) => t.key === editionCohortKey(edition))
    )
    .map((edition) => ({
      key: editionCohortKey(edition),
      label: `${edition.cohort_name} (archivado)`,
      audience: resolveEditionAudience(edition.audience),
      customCohortId: edition.cohort,
    }));

  // One in-prep edition per recipient cohort (Staff, Admins and each custom cohort).
  const targets: RecipientTarget[] = [...BASE_TARGETS, ...customTargets, ...orphanTargets];
  const [selectedKey, setSelectedKey] = useState(baseCohortKey(DEFAULT_EDITION_AUDIENCE));
  const target = targets.find((t) => t.key === selectedKey) ?? targets[0];
  const isCustomTarget = target.customCohortId !== null;
  const upcomingEdition = prepEditions.find((e) => editionCohortKey(e) === target.key) ?? null;

  const invalidate = (): void => {
    void queryCache.invalidateQueries(EDITIONS_QUERY_KEY);
  };

  // No eyebrow default: the backend derives it from the edition's send date.
  const buildDefaults = (): { subheading_override: string } => ({
    subheading_override: 'Hola {first_name}, aquí le compartimos las últimas novedades de BrainHi.',
  });

  const [doCreate, { isLoading: isCreating }] = useMutation(
    () =>
      createEdition({
        ...buildDefaults(),
        ...(target.customCohortId === null
          ? { audience: target.audience }
          : { cohort: target.customCohortId }),
      }),
    {
      onSuccess: () => {
        invalidate();
        Toast.success('Nueva edición creada.');
      },
      onError: (error) => {
        invalidate();
        Toast.error(getEditionActionErrorMessage(error, 'Error al crear la edición.'));
      },
    }
  );

  const [doAutoBuild, { isLoading: isAutoBuilding }] = useMutation(
    () => autoBuildEdition(target.audience),
    {
      onSuccess: (res) => {
        invalidate();
        if (res.status === 204) {
          Toast.warning(
            'No hay novedades nuevas en el catálogo para proponer. Crea la edición en blanco y añade novedades del catálogo manualmente.'
          );
        } else {
          Toast.success('Edición pre-construida con las novedades más recientes.');
        }
      },
      onError: () => {
        Toast.error('Error al auto-construir la edición.');
      },
    }
  );

  const [doArchive] = useMutation((id: string) => archiveEdition(id), {
    onSuccess: () => {
      invalidate();
      Toast.success('Edición archivada.');
    },
    onError: () => {
      Toast.error('Error al archivar.');
    },
  });

  const [doRestore, { isLoading: isRestoring }] = useMutation((id: string) => restoreEdition(id), {
    onSuccess: () => {
      invalidate();
      setSubView('prep');
      Toast.success('Edición reactivada como borrador.');
    },
    onError: () => {
      Toast.error('Error al reactivar.');
    },
  });

  if (isLoading) return <DotBricksLoading />;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sub-tab pills */}
      <div className="flex items-center flex-shrink-0 gap-1 px-5 py-3 bg-white border-b">
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full">
          <button
            type="button"
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
              subView === 'prep' ? 'bg-white shadow text-gray-800' : 'text-gray-500'
            }`}
            onClick={() => setSubView('prep')}
          >
            En preparación
          </button>
          <button
            type="button"
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
              subView === 'archived' ? 'bg-white shadow text-gray-800' : 'text-gray-500'
            }`}
            onClick={() => setSubView('archived')}
          >
            Archivadas · {archivedEditions.length}
          </button>
        </div>
        {subView === 'prep' && (
          <div className="flex items-center gap-1 p-1 ml-2 bg-gray-100 rounded-full">
            {targets.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  target.key === key ? 'bg-white shadow text-gray-800' : 'text-gray-500'
                }`}
                onClick={() => setSelectedKey(key)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* En preparación */}
      {subView === 'prep' && (
        <>
          {upcomingEdition ? (
            <div className="flex-1 overflow-hidden">
              <EditionComposer
                editionId={String(upcomingEdition.id)}
                onArchive={() => doArchive(String(upcomingEdition.id))}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center flex-1 p-8">
              <div className="max-w-md text-center">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5 border border-blue-200 border-dashed">
                  <Mail size={24} className="text-blue-700" />
                </div>
                <h2 className="mb-2 text-lg font-bold text-gray-900">
                  No hay una proxima edicion de {target.label} en preparacion
                </h2>
                {isCustomTarget ? (
                  <>
                    <p className="mb-6 text-sm leading-relaxed text-gray-500">
                      Crea la edición en blanco y añade novedades del catálogo manualmente; luego la
                      revisas, ajustas y apruebas. Llegará a las personas de este cohort.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary px-6 py-2 text-sm"
                      disabled={isCreating}
                      onClick={() => doCreate()}
                    >
                      {isCreating ? 'Creando…' : 'Crear edición en blanco'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="mb-6 text-sm leading-relaxed text-gray-500">
                      El escaneo diario de PRs alimenta el catalogo de novedades, pero la edicion no
                      se arma sola. Pre-construila ahora con las novedades del catalogo que aun no
                      se han enviado, o creala en blanco; luego la revisas, ajustas y apruebas.
                    </p>
                    <div className="flex flex-col items-center gap-3">
                      <button
                        type="button"
                        className="btn btn-primary px-6 py-2 text-sm"
                        disabled={isAutoBuilding || isCreating}
                        onClick={() => doAutoBuild()}
                      >
                        {isAutoBuilding ? 'Construyendo…' : 'Auto-construir edicion'}
                      </button>
                      <button
                        type="button"
                        className="hover:text-gray-600 text-xs text-gray-400 underline"
                        disabled={isCreating || isAutoBuilding}
                        onClick={() => doCreate()}
                      >
                        {isCreating ? 'Creando…' : 'O crear en blanco'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Archivadas */}
      {subView === 'archived' && (
        <div className="flex-1 p-6 overflow-y-auto">
          {archivedEditions.length === 0 ? (
            <p className="py-12 text-sm text-center text-gray-400">
              Nada archivado. Las ediciones que no llegan a enviarse aparecen aquí.
            </p>
          ) : (
            <>
              <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                <div
                  className="bg-gray-50 grid gap-4 px-5 py-3 text-xs font-bold tracking-widest text-gray-400 uppercase border-b"
                  style={{ gridTemplateColumns: '1fr 160px 130px' }}
                >
                  <div>Título</div>
                  <div>Creada</div>
                  <div />
                </div>
                {archivedEditions.map((edition) => {
                  const hasOpenEdition = prepEditions.some(
                    (e) => editionCohortKey(e) === editionCohortKey(edition)
                  );
                  return (
                    <div
                      key={edition.id}
                      className="last:border-0 grid items-center gap-4 px-5 py-4 border-b border-gray-100"
                      style={{ gridTemplateColumns: '1fr 160px 130px' }}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {edition.titulo || (
                            <span className="italic text-gray-400">Sin título</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">{edition.cohort_name}</p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {DateTime.fromISO(edition.created_at).toLocaleString(DateTime.DATE_MED)}
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost text-xs px-3 py-1.5 border border-gray-200 rounded-full text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        disabled={isRestoring || hasOpenEdition}
                        title={
                          hasOpenEdition
                            ? 'Ya hay una edición en preparación para este cohort'
                            : 'Reactivar edición'
                        }
                        onClick={() => doRestore(String(edition.id))}
                      >
                        Reactivar
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-gray-400">
                Las archivadas nunca salieron, así que no aparecen en Lanzamientos.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UpcomingTab;
